import { OndoMark } from "./OndoMark.tsx";

import "./loading.css";

/**
 * The registry's three ways of saying "not yet".
 *
 * Which one to reach for is a question about what is already on the screen,
 * not about how long the wait is:
 *
 * - `BootScreen` — nothing is drawn yet and no route has decided. Owns the
 *   viewport.
 * - `LoadingScreen` — a shell or panel is drawn and its contents are not. Owns
 *   the space the contents will fill.
 * - `LoadingNote` — the screen is readable and one part of it is still
 *   arriving. Owns one line.
 *
 * All three announce with `role="status"` and carry the caller's own message,
 * because the sentence is copy and belongs in the screen's constants file, not
 * in here. The mark beside it is decorative and hidden from assistive tech —
 * a screen reader that heard "image" before the message would be hearing the
 * spinner, which is the one part of a loading state nobody needs read aloud.
 */

/** The ONDO mark, animating. Sized by the wrapper that contains it. */
function LoadingMark() {
  return (
    <span className="loading-mark" aria-hidden="true">
      <OndoMark />
    </span>
  );
}

/** Shown while the session lookup is in flight, before any route can decide. */
export function BootScreen({ message }: { message: string }) {
  return (
    <main className="boot" role="status">
      <div className="boot__inner">
        <LoadingMark />
        <span className="loading__rule" aria-hidden="true" />
        <p className="loading__message">{message}</p>
      </div>
    </main>
  );
}

/** Shown in place of a screen's contents, inside a shell that is already drawn. */
export function LoadingScreen({ message }: { message: string }) {
  return (
    <div className="loading-screen" role="status">
      <LoadingMark />
      <span className="loading__rule" aria-hidden="true" />
      <p className="loading__message">{message}</p>
    </div>
  );
}

/**
 * One line, beside content that is already readable.
 *
 * A `<span>` rather than a `<p>`, because half its callers sit inside a row of
 * inline text — the collection's summary line puts it next to a count — and a
 * block element there would break the row.
 */
export function LoadingNote({ message }: { message: string }) {
  return (
    <span className="loading-note" role="status">
      <LoadingMark />
      <span className="loading__message">{message}</span>
    </span>
  );
}
